import React from 'react';
import { Chip } from '@mui/material';
import { OrgSubscriptionTag } from '../services/api';
import { isPayingOrg } from '../utils/org-payment-signal';
import { IS_BROKER_ADMIN } from '../config/admin-build';

/**
 * Shared presentation AND derivation for an org's billing/paying tag, used by the
 * Organizations list "Billing" column and the Usage Health "Billing" column +
 * paying filter (Trello #335). Kept in one place so the chip styling, the
 * "which tags count as paying" definition, and — since #377 — the EFT-aware
 * effective-tag derivation can't drift between the two consumers.
 *
 * They DID drift: `effectiveBillingTag` was added to OrganizationsPage for #332
 * (so an EFT payer, which has no Stitch subscription and therefore joins to
 * 'unpaid', reads as "Paid") and Usage Health was never migrated — it kept
 * rendering the raw `subscriptionTag`. Marcus saw real EFT-paying customers
 * labelled "Unpaid" on Usage Health while the Organizations table beside it
 * called the same orgs "Paid" (#377). The derivation now lives here and BOTH
 * tables call it.
 */

/**
 * The tags that count as PAYING for the Usage Health filter: a live self-serve
 * subscription ('paid') OR a comp'd/managed (invoiced-by-us) plan ('managed').
 * 'trial' (no charge taken yet) and 'unpaid' (no live subscription) are NOT
 * paying. Deliberately distinct from `org-payment-signal.isPayingOrg`, which is
 * revenue-focused (subscriptionTag==='paid' OR eftPayer, excludes managed) and
 * operates on a full Organization; this operates on a usage-health row's tag.
 */
export const PAYING_TAGS: ReadonlySet<OrgSubscriptionTag> = new Set<OrgSubscriptionTag>([
  'paid',
  'managed',
]);

/** True when the tag represents a paying org (paid or managed). undefined → false. */
export function isPayingTag(tag: OrgSubscriptionTag | undefined): boolean {
  return tag !== undefined && PAYING_TAGS.has(tag);
}

/**
 * The human label a tag renders as. Exported so a consumer that needs the
 * TEXT rather than the chip — e.g. the Organizations grid's Billing
 * `valueGetter`, which feeds the DataGrid's own column filter + sort — reads
 * the same label the chip shows, instead of re-rolling the mapping (#332).
 */
export function billingTagLabel(tag: OrgSubscriptionTag): string {
  switch (tag) {
    case 'paid':
      return 'Paid';
    case 'trial':
      return 'Trial';
    case 'unpaid':
      return 'Unpaid';
    case 'managed':
      return 'Managed';
    default:
      // Runtime schema drift (a tag the backend added before the frontend was
      // redeployed) must NOT fall through to a paying-status label — a neutral
      // "Unknown" so sales can't misread a non-paying org as paying.
      return 'Unknown';
  }
}

/**
 * The billing signals an effective-tag derivation needs. Deliberately a MINIMAL
 * structural shape rather than the full `Organization`, so BOTH data sources
 * satisfy it: an Organizations-list row AND a Usage Health row (which carries the
 * same four fields since the #377 backend passthrough).
 */
export interface BillingSignals {
  subscriptionTag?: OrgSubscriptionTag;
  eftPayer?: boolean;
  billingMode?: string;
  manualBillingStatus?: 'trial' | 'unpaid' | 'paid';
}

/**
 * The org's EFFECTIVE billing tag — the single derivation both the Billing chip
 * and the Billing filters read, per build.
 *
 * broker-AI: a PAYING org shows 'paid', delegating to the shared `isPayingOrg`
 * predicate (subscriptionTag === 'paid' OR eftPayer) — the SAME authoritative
 * rule the Referral Sources report + FSP commission use, so the EFT-payer
 * definition lives in ONE place. This is what catches the EFT payer whose
 * subscriptionTag joins to 'unpaid': it IS paying, matching the org detail
 * panel's buildOrgBillingSummary. Otherwise the backend subscriptionTag, falling
 * back to the managed billingMode (older backend).
 *
 * IA: the manually-set manualBillingStatus (undefined = untagged) — the sales-led
 * tenant has no self-serve subscription to auto-derive from.
 */
export function effectiveBillingTag(org: BillingSignals): OrgSubscriptionTag | undefined {
  if (IS_BROKER_ADMIN) {
    if (isPayingOrg(org)) return 'paid';
    if (org.subscriptionTag) return org.subscriptionTag;
    if (org.billingMode === 'managed') return 'managed';
    return undefined;
  }
  return org.manualBillingStatus;
}

/**
 * True when the org is paying, judged on its EFFECTIVE tag rather than the raw
 * subscription join — so an EFT payer counts. This is what the Usage Health
 * "Paying only" filter must use; filtering on the raw tag silently dropped every
 * EFT-paying org from the list (#377).
 */
export function isPayingBillingSignals(org: BillingSignals): boolean {
  return isPayingTag(effectiveBillingTag(org));
}

// Billing-cell text for an org with no effective tag: broker-AI falls back to
// the self-serve default; IA shows an em-dash until an admin tags the org.
// Exported so the tests pin the exact strings the grid filter matches on.
export const BILLING_CELL_SELF_SERVE = 'Self-serve';
export const BILLING_CELL_UNTAGGED = '—';

/**
 * The Billing cell's underlying VALUE — what the DataGrid's OWN column menu
 * (Filter / Sort) reads. It MUST equal the text the chip renders: the column's
 * `field` is the raw `subscriptionTag`, so without this getter the grid filter
 * matched the raw join value while the chip showed the EFT-aware one. Filtering
 * the column to "Paid" then returned only the self-serve payers and silently
 * dropped every EFT-paying org (Marcus, Trello #332 — 10 of 21 on prod).
 */
export function billingCellValue(org: BillingSignals): string {
  const tag = effectiveBillingTag(org);
  if (tag) return billingTagLabel(tag);
  return IS_BROKER_ADMIN ? BILLING_CELL_SELF_SERVE : BILLING_CELL_UNTAGGED;
}

/** Single Billing-tag chip shared by the Organizations + Usage Health tables. */
export function renderBillingTagChip(tag: OrgSubscriptionTag): React.ReactElement {
  const label = billingTagLabel(tag);
  switch (tag) {
    case 'paid':
      return <Chip label={label} size="small" color="success" variant="outlined" />;
    case 'trial':
      return <Chip label={label} size="small" color="info" variant="outlined" />;
    case 'unpaid':
      return <Chip label={label} size="small" color="warning" variant="outlined" />;
    case 'managed':
      return <Chip label={label} size="small" color="default" variant="outlined" />;
    default:
      return <Chip label={label} size="small" color="default" variant="outlined" />;
  }
}
